import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RegistrationApiService,
  ClinicInfoRequest,
} from "../../registration/api/RegistrationApiService";
import { OnboardingApiService } from "../../onboarding/api/OnboardingApiService";
import { isOnboardingFlow } from "../utils/onboardingFlow";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import ProfileProgressBar from "../components/ProfileProgressBar";
import { useDoctorInfoWithAutoRefresh } from "../contexts/DoctorInfoContext";
import { useRegistrationNavigation } from "../../../hooks/useRegistrationNavigation";
import { useNavigate, useLocation } from "react-router-dom";
import { useProgressWithAutoRefresh, triggerProgressRefresh } from "../../../shared/contexts/ProgressContext";
import { skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import { normalizeNumericInput } from "../../../shared/utils/numberUtils";
import LocationPicker from "../../../shared/components/ui/LocationPicker";
import CitySelect from "../../../shared/components/ui/CitySelect";
import { useUnsavedFormGuard } from "../../../shared/utils/unsavedChanges";

interface ClinicInfoRegistrationScreenProps {
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
}

interface ClinicFormData {
  name: string; // Clinic name
  governorate: string; // Governorate
  area_id: string; // City area UUID
  neighborhood: string; // Neighborhood (will be converted to number)
  street: string; // Street (will be converted to number)
  house_number: string; // House number (will be converted to number)
  nearest_landmark: string; // Nearest landmark
  latitude: string | null; // Latitude as string (will be converted to number)
  longitude: string | null; // Longitude as string (will be converted to number)
  location_address: string | null; // Human-readable address (for display only, not sent to API)
  // Work hours — empty until the doctor explicitly chooses. Pre-selecting
  // "morning" let doctors skip the question and silently submit a wrong shift.
  clinic_work_hours: "morning" | "evening" | "";
  tax_number: string; // Optional tax number
  applicant_notes: string; // Optional notes
}

const GOVERNORATE_API_MAP: Record<string, string> = {
  Baghdad: "baghdad",
  Basrah: "basra",
  Nineveh: "nineveh",
  Maysan: "maysan",
  Diyala: "diyala",
  "Al Anbar": "anbar",
  Kirkuk: "kirkuk",
  Saladin: "saladin",
  Duhok: "dohuk",
  Erbil: "erbil",
  Sulaymaniyah: "sulaymaniyah",
  Halabja: "halabja",
  Najaf: "najaf",
  Karbala: "karbala",
  Babylon: "babil",
  Wasit: "wasit",
  "Al Qadisiyah": "qadisiyah",
  "Al Muthanna": "muthanna",
  Dhiqar: "dhi_qar",
};

const GOVERNORATES = [
  "Baghdad",
  "Basrah",
  "Nineveh",
  "Maysan",
  "Diyala",
  "Al Anbar",
  "Kirkuk",
  "Saladin",
  "Duhok",
  "Erbil",
  "Sulaymaniyah",
  "Halabja",
  "Najaf",
  "Karbala",
  "Babylon",
  "Wasit",
  "Al Qadisiyah",
  "Al Muthanna",
  "Dhiqar",
];

const KURDISTAN_GOVERNORATES = ["Erbil", "Sulaymaniyah", "Duhok", "Halabja"];

const INITIAL_FORM_DATA: ClinicFormData = {
  name: "",
  governorate: "",
  area_id: "",
  neighborhood: "",
  street: "",
  house_number: "",
  nearest_landmark: "",
  latitude: null,
  longitude: null,
  location_address: null,
  clinic_work_hours: "",
  tax_number: "",
  applicant_notes: "",
};

// Field label translation keys
const FIELD_LABEL_KEYS: Record<keyof ClinicFormData, string> = {
  name: "clinic_name",
  governorate: "governorate",
  area_id: "city",
  neighborhood: "neighborhood",
  street: "street",
  house_number: "house_number",
  nearest_landmark: "nearest_landmark",
  latitude: "latitude",
  longitude: "longitude",
  location_address: "location_address",
  clinic_work_hours: "clinic_working_hours",
  tax_number: "tax_number",
  applicant_notes: "applicant_notes",
};

const REQUIRED_FIELDS: Array<keyof ClinicFormData> = [
  "name",
  "governorate",
  "area_id",
  "neighborhood",
  "street",
  "house_number",
  "nearest_landmark",
  "latitude",
  "longitude",
  "clinic_work_hours",
];

const normalizeString = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const extractDate = (value: unknown): string => {
  if (typeof value !== "string" || !value) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
};

const isValidUUID = (value: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

const mapGovernorateToApi = (displayName: string): string | undefined => {
  if (!displayName) {
    return undefined;
  }

  return GOVERNORATE_API_MAP[displayName] ?? displayName;
};

const mapGovernorateFromApiValue = (apiValue?: string | null): string => {
  if (!apiValue) {
    return "";
  }

  const entry = Object.entries(GOVERNORATE_API_MAP).find(
    ([, value]) => value === apiValue
  );
  return entry ? entry[0] : apiValue;
};

const buildClinicRegistrationPayload = (
  formData: ClinicFormData,
  doctorId?: string
): ClinicInfoRequest => {
  return {
    name: formData.name.trim(),
    governorate:
      mapGovernorateToApi(formData.governorate) || formData.governorate,
    area_id: formData.area_id,
    neighborhood: parseInt(formData.neighborhood, 10),
    street: parseInt(formData.street, 10),
    house_number: parseInt(formData.house_number, 10),
    nearest_landmark: formData.nearest_landmark.trim(),
    latitude: formData.latitude ? parseFloat(formData.latitude) : null,
    longitude: formData.longitude ? parseFloat(formData.longitude) : null,
    // validateForm guarantees a choice was made before the payload is built
    clinic_work_hours: formData.clinic_work_hours as "morning" | "evening",
    tax_number: formData.tax_number.trim() || null,
    applicant_notes: formData.applicant_notes.trim() || null,
    doctor_id: doctorId || null,
  };
};

/** The fields a doctor edits on this screen, in a stable form for comparison. */
function clinicInfoSnapshot(f: any): string {
  return JSON.stringify([
    f.name, f.governorate, f.area_id, f.neighborhood, f.street, f.house_number,
    f.nearest_landmark, f.latitude, f.longitude, f.clinic_work_hours, f.applicant_notes,
  ]);
}

const ClinicInfoRegistrationScreen: React.FC<
  ClinicInfoRegistrationScreenProps
> = ({ onNext, onBack, onError }) => {
  const [formData, setFormData] = useState<ClinicFormData>(INITIAL_FORM_DATA);
  // Ask before a design switch discards typed changes (both designs share this guard).
  useUnsavedFormGuard(formData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [showGovernoratePopup, setShowGovernoratePopup] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const hasLoadedData = useRef(false);
  // What the saved request says, to tell "continue unchanged" from "edit" (see handleSubmit).
  const loadedSnapshot = useRef<string | null>(null);

  const registrationApi = useMemo(() => new RegistrationApiService(), []);
  const onboardingApi = useMemo(() => new OnboardingApiService(), []);
  const { refreshDoctorInfo } = useDoctorInfoWithAutoRefresh(false);
  const { navigateAfterStepSubmission } = useRegistrationNavigation();
  const { t, isRTL } = useLanguage();
  const { progress, refreshProgress } = useProgressWithAutoRefresh();
  const navigate = useNavigate();
  const location = useLocation();

  const isOnboarding = isOnboardingFlow(progress);
  const fromReview = (location.state as any)?.fromReview === true;
  const activeRequest = progress?.active_requests?.find(
    (r: any) => r.request_type === (isOnboarding ? 'onboarding' : 'registration')
  ) || null;
  const activeRequestId = activeRequest?.request_id || null;
  const showCancelInsteadOfBack =
    !fromReview && !isOnboarding && !!activeRequest && activeRequest.status === "draft";

  // Load existing data when editing (coming from review or when request already exists)
  useEffect(() => {
    if (hasLoadedData.current || !progress) return;
    hasLoadedData.current = true;

    const loadExistingData = async () => {
      setDataLoading(true);
      try {
        const requestType = isOnboarding ? 'onboarding' : 'registration';
        const activeRequest = progress?.active_requests?.find(
          (r: any) => r.request_type === requestType
        );
        if (!activeRequest) {
          setDataLoading(false);
          return;
        }

        const reqId = activeRequest.request_id;
        const result = isOnboarding
          ? await onboardingApi.getOnboardingWithAttachments(reqId)
          : await registrationApi.getRegistrationWithAttachments(reqId);

        const data = result.isSuccess ? result.data : null;

        if (data) {
          const loaded = {
            name: data.name || "",
            governorate: mapGovernorateFromApiValue(data.governorate),
            area_id: data.area?.id || "",
            neighborhood: data.neighborhood != null ? String(data.neighborhood) : "",
            street: data.street != null ? String(data.street) : "",
            house_number: data.house_number != null ? String(data.house_number) : "",
            nearest_landmark: data.nearest_landmark || "",
            latitude: data.latitude != null ? String(data.latitude) : null,
            longitude: data.longitude != null ? String(data.longitude) : null,
            location_address: null,
            clinic_work_hours: data.clinic_work_hours || "",
            tax_number: data.tax_number || "",
            applicant_notes: data.applicant_notes || "",
          };
          loadedSnapshot.current = clinicInfoSnapshot(loaded);
          setFormData(loaded);
        }
      } catch (error) {
        console.error("[ClinicInfoScreen] Error loading existing data:", error);
      } finally {
        setDataLoading(false);
      }
    };

    loadExistingData();
  }, [isOnboarding, registrationApi, onboardingApi, progress]);

  const handleInputChange = <Field extends keyof ClinicFormData>(
    field: Field,
    value: ClinicFormData[Field]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field as string]) {
      setErrors((prev) => ({ ...prev, [field as string]: "" }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check required string fields
    REQUIRED_FIELDS.forEach((field) => {
      const value = formData[field];

      // Special handling for latitude and longitude (nullable fields)
      if (field === "latitude" || field === "longitude") {
        if (value === null || value === "") {
          const fieldLabel = t(FIELD_LABEL_KEYS[field]);
          newErrors[field as string] = t("field_is_required").replace("{field}", fieldLabel);
        }
      } else if (typeof value === "string" && !value.trim()) {
        const fieldLabel = t(FIELD_LABEL_KEYS[field]);
        newErrors[field as string] = t("field_is_required").replace("{field}", fieldLabel);
      }
    });

    // Validate governorate
    if (formData.governorate && !GOVERNORATES.includes(formData.governorate)) {
      newErrors.governorate = t("invalid_governorate");
    }

    // Validate name length
    if (formData.name.trim().length < 2 || formData.name.trim().length > 255) {
      newErrors.name = t("clinic_name_length_error");
    }

    // Validate city selection
    if (!formData.area_id) {
      newErrors.area_id = t("city_required") || "Please select a city";
    }

    // Validate nearest landmark length
    if (
      formData.nearest_landmark.trim().length < 2 ||
      formData.nearest_landmark.trim().length > 255
    ) {
      newErrors.nearest_landmark = t("nearest_landmark_length_error");
    }

    // Validate integer fields
    if (formData.neighborhood && isNaN(parseInt(formData.neighborhood, 10))) {
      newErrors.neighborhood = t("neighborhood_must_be_number");
    }

    if (formData.street && isNaN(parseInt(formData.street, 10))) {
      newErrors.street = t("street_must_be_number");
    }

    if (formData.house_number && isNaN(parseInt(formData.house_number, 10))) {
      newErrors.house_number = t("house_number_must_be_number");
    }

    // Validate latitude and longitude if provided
    if (formData.latitude !== null && formData.latitude !== "") {
      const lat = parseFloat(formData.latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        newErrors.latitude = t("invalid_latitude");
      }
    }
    if (formData.longitude !== null && formData.longitude !== "") {
      const lng = parseFloat(formData.longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        newErrors.longitude = t("invalid_longitude");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log("🚀 [DEBUG] handleSubmit called");
    console.log("📝 [DEBUG] Current form data:", formData);

    if (!validateForm()) {
      console.log("❌ [DEBUG] Form validation failed");
      console.log("🚨 [DEBUG] Current errors:", errors);
      return;
    }

    console.log("✅ [DEBUG] Form validation passed");
    setLoading(true);

    try {
      console.log("🔄 [DEBUG] Getting fresh doctor info...");
      // Get doctor_id from doctorInfo
      const freshDoctorInfo = await refreshDoctorInfo();
      console.log("👤 [DEBUG] Fresh doctor info:", freshDoctorInfo);
      console.log(
        "🔍 [DEBUG] Doctor info structure:",
        freshDoctorInfo?.doctor_info
      );
      console.log(
        "👤 [DEBUG] Doctor info structure:",
        freshDoctorInfo?.doctor_info
      );

      // Extract doctor ID from doctor_info.id (the correct location)
      const doctorId = freshDoctorInfo?.doctor_info?.id;
      console.log(
        "🆔 [DEBUG] Extracted doctor ID from doctor_info.id:",
        doctorId
      );
      console.log(
        "🔍 [DEBUG] doctor_info.id specifically:",
        freshDoctorInfo?.doctor_info?.id
      );
      console.log(
        "🔍 [DEBUG] doctor_info object:",
        JSON.stringify(freshDoctorInfo?.doctor_info, null, 2)
      );

      // Check doctor_info for any ID fields as fallback
      const doctorInfoId = freshDoctorInfo?.doctor_info?.id;
      console.log(
        "🔍 [DEBUG] doctor_info.id (should be undefined):",
        doctorInfoId
      );

      // Try to find any field that might contain an ID
      const allDoctorInfoValues = freshDoctorInfo?.doctor_info
        ? Object.entries(freshDoctorInfo.doctor_info)
        : [];
      console.log(
        "🔍 [DEBUG] All doctor_info key-value pairs:",
        allDoctorInfoValues
      );

      if (!doctorId) {
        console.log("❌ [DEBUG] Doctor ID not found in doctor_info");
        console.log(
          "🔍 [DEBUG] Available fields in doctor_info:",
          Object.keys(freshDoctorInfo?.doctor_info || {})
        );
        console.log(
          "🔍 [DEBUG] Available fields in doctor_info:",
          Object.keys(freshDoctorInfo?.doctor_info || {})
        );

        // Try using phone_number as ID fallback if it exists
        const fallbackId =
          freshDoctorInfo?.doctor_info?.phone_number ||
          freshDoctorInfo?.doctor_info?.phone_number;
        if (fallbackId) {
          console.log(
            "🔄 [DEBUG] Using phone_number as fallback ID:",
            fallbackId
          );
          // Continue with fallbackId
        } else {
          onError("Doctor ID not found. Please try logging in again.");
          return;
        }
      }

      console.log("📦 [DEBUG] Building clinic registration payload...");
      const finalDoctorId =
        doctorId ||
        freshDoctorInfo?.doctor_info?.phone_number ||
        freshDoctorInfo?.doctor_info?.phone_number;
      console.log(
        "🆔 [DEBUG] Final doctor ID to use (will be set to null for API):",
        finalDoctorId
      );
      console.log("⏰ [DEBUG] Working hours:", formData.clinic_work_hours);

      const payload = buildClinicRegistrationPayload(formData, finalDoctorId);
      console.log("📤 [DEBUG] Clinic registration payload:", payload);

      console.log("🌐 [DEBUG] Submitting clinic info to API...");
      console.log("📋 [DEBUG] isOnboarding:", isOnboarding);
      console.log("📋 [DEBUG] Existing request ID:", activeRequestId);

      // The server has no doctor-side update route for onboarding requests (PUT answers 405), so an
      // existing onboarding request cannot be edited from here: continue when nothing changed (e.g. the
      // doctor came back from the documents step), otherwise say so instead of failing with a 405.
      if (activeRequestId && isOnboarding) {
        if (loadedSnapshot.current !== null && clinicInfoSnapshot(formData) === loadedSnapshot.current) {
          skipNextStepRedirect();
          navigate(fromReview ? '/review' : '/onboarding_photo');
        } else {
          onError(t("onboarding_info_not_editable"));
        }
        return;
      }

      // Use PUT if updating existing request, POST if creating new
      let result;
      if (activeRequestId) {
        result = isOnboarding
          ? await onboardingApi.updateClinicInfo(activeRequestId, payload)
          : await registrationApi.updateClinicInfo(activeRequestId, payload);
      } else {
        result = isOnboarding
          ? await onboardingApi.submitClinicInfo(payload)
          : await registrationApi.submitClinicInfo(payload);
      }
      console.log("📥 [DEBUG] API response:", result);

      if (result.isSuccess && result.data) {
        console.log("✅ [DEBUG] Clinic info submission successful");

        // Refresh the cached progress BEFORE navigating so the photo step can read
        // the (possibly just-created) registration request id from active_requests.
        // Without this the photo screen mounts with a stale cache and uploads fail
        // with "No request ID available for upload".
        try {
          await refreshProgress(true);
        } catch (e) {
          console.warn("Progress refresh after clinic-info submit failed (continuing):", e);
        }

        skipNextStepRedirect();
        if (fromReview) {
          // Coming from review page - go back to review
          navigate('/review');
        } else if (isOnboarding) {
          navigate('/onboarding_photo');
        } else {
          navigate('/clinic_photo');
        }
      } else {
        console.log("❌ [DEBUG] API submission failed");
        onError(result.error || "Failed to submit clinic information");
      }
    } catch (error) {
      console.log("💥 [DEBUG] Exception caught in handleSubmit");
      console.log("🚨 [DEBUG] Error object:", error);
      console.log(
        "📋 [DEBUG] Error message:",
        error instanceof Error ? error.message : "Unknown error"
      );
      console.log(
        "📚 [DEBUG] Error stack:",
        error instanceof Error ? error.stack : "No stack trace"
      );
      onError(
        error instanceof Error
          ? error.message
          : "Failed to submit clinic information"
      );
    } finally {
      console.log(
        "🏁 [DEBUG] handleSubmit finally block - setting loading to false"
      );
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!activeRequestId) return;
    setShowCancelDialog(false);
    setCancelling(true);
    setCancelError(null);
    try {
      const result = await registrationApi.cancelRegistrationRequest(activeRequestId);
      if (result.isSuccess) {
        triggerProgressRefresh();
        navigate("/home", { replace: true });
      } else {
        setCancelError(result.error || t("failed_to_cancel_request") || "Failed to cancel request");
      }
    } catch (error) {
      setCancelError(error instanceof Error ? error.message : t("failed_to_cancel_request") || "Failed to cancel request");
    } finally {
      setCancelling(false);
    }
  };

  const handleBackOrCancel = showCancelInsteadOfBack ? () => setShowCancelDialog(true) : onBack;

  if (dataLoading) {
    return (
      <ResponsiveFormLayout
        onBack={handleBackOrCancel}
        showBackButton={true}
        showLanguageToggle
        icon="/ima_logo.png"
        iconSize="w-32 h-32"
        title={t("clinic_information")}
        subtitle={t("clinic_location_operational")}
        progressValue={60}
        maxHeight="80vh"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2E5D7D] border-t-transparent mb-4" />
          <p className="text-gray-600 text-center">
            {t("loading_clinic_info")}
          </p>
        </div>
      </ResponsiveFormLayout>
    );
  }

  const continueButton = (
    <div className="flex gap-3 w-full">
      <button
        onClick={handleBackOrCancel}
        disabled={loading || cancelling}
        className={`min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold transition-all duration-200 ${
          showCancelInsteadOfBack
            ? "border border-red-300 text-red-600 hover:bg-red-50"
            : "border border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        {showCancelInsteadOfBack ? (t("cancel_request") || "Cancel Request") : (t("back") || "Back")}
      </button>
      <button
        onClick={() => {
          console.log("🖱️ [DEBUG] Continue button clicked!");
          handleSubmit();
        }}
        disabled={loading}
        className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading
            ? "opacity-50 cursor-not-allowed"
            : "hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg"
        }`}
        style={{ backgroundColor: "#2E5D7D" }}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            <span>{t("loading")}</span>
          </div>
        ) : (
          <span>{t("continue")}</span>
        )}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={handleBackOrCancel}
      showBackButton={true}
      showLanguageToggle
      icon="/ima_logo.png"
      iconSize="w-32 h-32"
      title={t("clinic_information")}
      subtitle={t("clinic_location_operational")}
      actions={continueButton}
      progressValue={60}
      maxHeight="80vh"
    >
      <ProfileProgressBar onboarding={isOnboarding} />

      <div className="w-full space-y-6">
        {/* Clinic Name */}
        <section className="space-y-3">
          <div>
            <label
              className={`block text-sm font-medium text-gray-700 mb-2 ${
                isRTL ? "text-right" : "text-left"
              }`}
            >
              {t("clinic_name") || "Clinic Name"}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder={t("enter_clinic_name")}
              className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                errors.name
                  ? "border-red-500"
                  : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>
        </section>

        {/* Location Information */}
        <section className="space-y-3">
          <h3
            className={`text-base font-semibold text-black ${
              isRTL ? "text-right" : "text-left"
            }`}
          >
            {t("clinic_address") || "Clinic Address"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Governorate */}
            <div>
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("governorate")} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.governorate}
                onChange={(e) => {
                  const selectedGovernorate = e.target.value;
                  handleInputChange("governorate", selectedGovernorate);
                  handleInputChange("area_id", ""); // Reset city when governorate changes
                  if (selectedGovernorate && KURDISTAN_GOVERNORATES.includes(selectedGovernorate)) {
                    setShowGovernoratePopup(true);
                  }
                }}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.governorate
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              >
                <option value="">{t("select_governorate")}</option>
                {GOVERNORATES.map((governorate) => (
                  <option key={governorate} value={governorate}>
                    {t(governorate) || governorate}
                  </option>
                ))}
              </select>
              {errors.governorate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.governorate}
                </p>
              )}
            </div>

            {/* City */}
            <div>
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("city") || "City"} <span className="text-red-500">*</span>
              </label>
              <CitySelect
                value={formData.area_id}
                governorate={mapGovernorateToApi(formData.governorate) || formData.governorate}
                onChange={(id) => handleInputChange("area_id", id)}
                isRTL={isRTL}
                disabled={!formData.governorate}
                error={errors.area_id}
                placeholder={t("select_city") || "Select City"}
                loadingText={t("loading") || "Loading..."}
              />
              {errors.area_id && (
                <p className="text-red-500 text-sm mt-1">{errors.area_id}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Neighborhood */}
            <div>
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("neighborhood") || "Neighborhood"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.neighborhood}
                onChange={(e) => {
                  const converted = normalizeNumericInput(e.target.value);
                  handleInputChange("neighborhood", converted);
                }}
                placeholder={t("neighborhood_placeholder")}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.neighborhood
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.neighborhood && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.neighborhood}
                </p>
              )}
            </div>

            {/* Street */}
            <div>
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("street") || "Street"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.street}
                onChange={(e) => {
                  const converted = normalizeNumericInput(e.target.value);
                  handleInputChange("street", converted);
                }}
                placeholder={t("street_placeholder")}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.street
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.street && (
                <p className="text-red-500 text-sm mt-1">{errors.street}</p>
              )}
            </div>

            {/* House Number */}
            <div>
              <label
                className={`block text-sm font-medium text-gray-700 mb-2 ${
                  isRTL ? "text-right" : "text-left"
                }`}
              >
                {t("house_number") || "House Number"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.house_number}
                onChange={(e) => {
                  const converted = normalizeNumericInput(e.target.value);
                  handleInputChange("house_number", converted);
                }}
                placeholder={t("house_number_placeholder")}
                className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                  errors.house_number
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                }`}
              />
              {errors.house_number && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.house_number}
                </p>
              )}
            </div>
          </div>

          {/* Nearest Landmark */}
          <div>
            <label
              className={`block text-sm font-medium text-gray-700 mb-2 ${
                isRTL ? "text-right" : "text-left"
              }`}
            >
              {t("nearest_landmark")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nearest_landmark}
              onChange={(e) =>
                handleInputChange("nearest_landmark", e.target.value)
              }
              placeholder={t("enter_nearest_landmark")}
              className={`w-full px-4 py-3 border rounded-xl transition-colors duration-200 ${
                errors.nearest_landmark
                  ? "border-red-500"
                  : "border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
              }`}
            />
            {errors.nearest_landmark && (
              <p className="text-red-500 text-sm mt-1">
                {errors.nearest_landmark}
              </p>
            )}
          </div>

          {/* Location Coordinates */}
          <div>
            <label
              className={`block text-sm font-medium text-gray-700 mb-2 ${
                isRTL ? "text-right" : "text-left"
              }`}
            >
              {t("clinic_location") || "Clinic Location"} <span className="text-red-500">*</span>
            </label>

            {/* Display selected location or button to open modal */}
            {formData.latitude && formData.longitude ? (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-sm text-green-800 font-medium mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t("location_selected") || "Location Selected"}
                      </div>
                      {formData.location_address ? (
                        <div className="text-sm text-gray-800">
                          <p className="font-medium mb-1">{formData.location_address}</p>
                          <p className="text-xs text-gray-500 font-mono">
                            {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                          </p>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 font-mono">
                          {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          latitude: null,
                          longitude: null,
                          location_address: null,
                        }));
                      }}
                      className="text-red-500 hover:text-red-700 transition-colors p-1"
                      title={t("clear_location") || "Clear location"}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsLocationPickerOpen(true)}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>{t("change_location") || "Change Location"}</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsLocationPickerOpen(true)}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t("select_location_on_map") || "Select Location on Map"}</span>
              </button>
            )}

            {(errors.latitude || errors.longitude) && (
              <p className="text-red-500 text-sm mt-1">{errors.latitude || errors.longitude}</p>
            )}

            {/* Location Picker Modal */}
            <LocationPicker
              value={formData.latitude && formData.longitude ? {
                lat: formData.latitude,
                lng: formData.longitude,
                address: formData.location_address || undefined
              } : null}
              onChange={(location) => {
                if (location) {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: location.lat,
                    longitude: location.lng,
                    location_address: location.address || null,
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: null,
                    longitude: null,
                    location_address: null,
                  }));
                }
              }}
              error={errors.latitude || errors.longitude}
              isOpen={isLocationPickerOpen}
              onClose={() => setIsLocationPickerOpen(false)}
              governorate={formData.governorate}
            />
          </div>
        </section>

        {/* Working Hours */}
        <section className="space-y-3">
          <h3
            className={`text-base font-semibold text-black ${
              isRTL ? "text-right" : "text-left"
            }`}
          >
            {t("clinic_working_hours") || "Working Hours"}
          </h3>
          <div>
            <div
              className={`flex items-center gap-6 ${
                isRTL ? "justify-end" : "justify-start"
              }`}
            >
              <label
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="clinic_work_hours"
                  checked={formData.clinic_work_hours === "morning"}
                  onChange={() =>
                    handleInputChange("clinic_work_hours", "morning")
                  }
                  className="w-5 h-5 text-[#2E5D7D] border-gray-300 focus:ring-[#2E5D7D] focus:ring-2"
                />
                <span className="text-sm text-gray-700">
                  {t("morning") || "Morning"}
                </span>
              </label>
              <label
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="clinic_work_hours"
                  checked={formData.clinic_work_hours === "evening"}
                  onChange={() =>
                    handleInputChange("clinic_work_hours", "evening")
                  }
                  className="w-5 h-5 text-[#2E5D7D] border-gray-300 focus:ring-[#2E5D7D] focus:ring-2"
                />
                <span className="text-sm text-gray-700">
                  {t("evening") || "Evening"}
                </span>
              </label>
            </div>
            {errors.clinic_work_hours && (
              <p className="text-red-500 text-sm mt-2">
                {errors.clinic_work_hours}
              </p>
            )}
          </div>
        </section>

        {/* Applicant Notes (Optional) */}
        {/* The tax number input was removed from this flow. The field is still
            carried through form state, the payload and the backend (all optional),
            so it can be surfaced again by restoring an input here. */}
        <section className="space-y-3">
          <div>
            <label
              className={`block text-sm font-medium text-gray-700 mb-2 ${
                isRTL ? "text-right" : "text-left"
              }`}
            >
              {t("applicant_notes") || "Notes (Optional)"}
            </label>
            <textarea
              value={formData.applicant_notes}
              onChange={(e) =>
                handleInputChange("applicant_notes", e.target.value)
              }
              placeholder={t("add_additional_notes")}
              rows={3}
              className="w-full px-4 py-3 border rounded-xl transition-colors duration-200 border-gray-300 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
            />
          </div>
        </section>
      </div>

      {/* Cancel Request Dialog */}
      {showCancelDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 9999999 }}
        >
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-4">
              <span className="text-5xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
              {t("confirm_cancel_request") || "Cancel Request?"}
            </h3>
            <p className="text-gray-600 mb-6 text-sm text-center">
              {t("cancel_request_message") || "Are you sure you want to cancel this request? This action cannot be undone."}
            </p>
            {cancelError && (
              <p className="text-red-600 text-sm mb-4 text-center">{cancelError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelDialog(false)}
                disabled={cancelling}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
              >
                {t("no_keep_it") || "No, Keep It"}
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={cancelling}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-all flex items-center justify-center gap-2"
              >
                {cancelling && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
                <span>{t("yes_cancel_it") || "Yes, Cancel It"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Governorate Not Available Popup */}
      {showGovernoratePopup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 9999999 }}
          onClick={() => {
            setShowGovernoratePopup(false);
            navigate('/home');
          }}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center">
              {/* Warning Icon */}
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-amber-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t("service_not_available") || "Service Not Available"}
              </h3>

              {/* Message */}
              <p className="text-gray-600 mb-6">
                {t("governorate_not_available_message") ||
                  "The service is currently not available in your governorate. Please visit the governorate branch for registration."}
              </p>

              {/* Redirect to Home Button */}
              <button
                onClick={() => {
                  setShowGovernoratePopup(false);
                  navigate('/home');
                }}
                className="w-full px-6 py-3 bg-[#2E5D7D] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity"
              >
                {t("understood") || "Understood"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ResponsiveFormLayout>
  );
};

export default ClinicInfoRegistrationScreen;
